import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Stack } from '@mui/material';
import { setActiveChapterId } from '../../../../../../../features/projects/projectsSlice';
import ChapterCard from './ChapterCard/ChapterCard';
import EmptyState from './EmptyState';

const ChapterList = ({
  chapters,
  onChapterClick = () => {},
  onSceneAdded,
  onAddScene,
  suppressSceneLoading = false,
}) => {
  const dispatch = useDispatch();
  const [visibleChapters, setVisibleChapters] = useState(new Set());
  const [activeChapters, setActiveChapters] = useState(new Set());

  // Publish the topmost visible chapter so other panels (e.g. right sidebar notes)
  // know which chapter the user is currently reading.
  useEffect(() => {
    if (visibleChapters.size === 0) {
      return;
    }
    const topIndex = Math.min(...Array.from(visibleChapters));
    const topChapter = chapters[topIndex];
    const chapterId = topChapter?.id || topChapter?._id || null;
    if (chapterId) {
      dispatch(setActiveChapterId(chapterId));
    }
  }, [visibleChapters, chapters, dispatch]);

  // Track which chapters are visible
  const handleVisibilityChange = useCallback((chapterIndex, isVisible) => {
    setVisibleChapters((prev) => {
      // Only update if the visibility actually changed
      const wasVisible = prev.has(chapterIndex);
      if (wasVisible === isVisible) {
        return prev; // No change, return same reference to prevent re-render
      }

      const next = new Set(prev);
      if (isVisible) {
        next.add(chapterIndex);
      } else {
        next.delete(chapterIndex);
      }
      return next;
    });
  }, []);

  // Create stable callbacks using useMemo - only recreate when chapters length changes
  const visibilityCallbacks = useMemo(() => {
    return chapters.reduce((acc, _, index) => {
      // Create a stable callback for each chapter index
      acc[index] = (isVisible) => handleVisibilityChange(index, isVisible);
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters.length]); // Only recreate when chapters length changes, not when handleVisibilityChange changes

  // Determine which chapters should be active (visible + one before + one after)
  useEffect(() => {
    if (visibleChapters.size === 0) {
      setActiveChapters(new Set());
      return;
    }

    const active = new Set();
    const visibleArray = Array.from(visibleChapters).sort((a, b) => a - b);

    visibleArray.forEach((index) => {
      // Add the visible chapter
      active.add(index);
      // Add one before (if exists)
      if (index > 0) {
        active.add(index - 1);
      }
      // Add one after (if exists)
      if (index < chapters.length - 1) {
        active.add(index + 1);
      }
    });

    setActiveChapters(active);
  }, [visibleChapters, chapters.length]);

  if (chapters.length === 0) {
    return <EmptyState />;
  }

  return (
    <Stack spacing={2}>
      {chapters.map((chapter, index) => {
        const isActive = activeChapters.has(index);
        return (
          <ChapterCard
            key={chapter.id || index}
            chapter={chapter}
            index={index}
            onChapterClick={onChapterClick}
            onSceneAdded={onSceneAdded}
            onAddScene={onAddScene}
            suppressSceneLoad={suppressSceneLoading}
            isActive={isActive}
            onVisibilityChange={visibilityCallbacks[index]}
          />
        );
      })}
    </Stack>
  );
};

/* eslint-disable react/require-default-props */
ChapterList.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      wordCount: PropTypes.number,
      sceneCount: PropTypes.number,
    })
  ).isRequired,
  onChapterClick: PropTypes.func,
  onSceneAdded: PropTypes.func,
  onAddScene: PropTypes.func,
  suppressSceneLoading: PropTypes.bool,
};
/* eslint-enable react/require-default-props */

export default ChapterList;
